import { makeStyles } from '@material-ui/core/styles';
import { Drawer, Box, Typography, styled, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useComposeEmail } from 'providersV2/compose-email/context';
import { RolesNames, TextColor } from '@setvi/shared/enums';
import { EmailIconV2 } from '@setvi/shared/components/sicons';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { CustomMenuVariant, MenuItems } from './MenuItems';
import { MenuButton } from './MenuItems/Button';

export const drawerWidth = 250;
export const collapsedDrawerWidth = 80;

const useStyles = makeStyles<Theme, { newDesign?: boolean }>(theme => ({
  toolbarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...theme.mixins.toolbar
  },
  drawer: {
    position: 'relative',
    border: 'none',
    whiteSpace: 'nowrap',
    background: '#F7F9FA',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerCollapsed: {
    width: 'fit-content',
    backgroundColor: ({ newDesign }) => (newDesign ? 'white' : '#f8f9fa')
  },
  logo: {
    maxHeight: '50px',
    maxWidth: '75px',
    margin: '0 auto',
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  composeEmailWrapper: {
    borderBottom: '1px solid #eef0f2',
    padding: theme.spacing(2, 0)
  }
}));

const SidebarContent = styled(Box)({
  color: TextColor.SlateGrey,
  width: collapsedDrawerWidth,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between'
});

interface ISidebar {
  newDesign?: boolean;
  collapsedDrawer: boolean;
  expandedSidebarMenuItem: any;
  setExpandedSidebarMenuItem: (expandedSidebarMenuItem: any) => void;
}

export const Sidebar = ({
  newDesign,
  collapsedDrawer,
  expandedSidebarMenuItem,
  setExpandedSidebarMenuItem
}: ISidebar) => {
  const classes = useStyles({ newDesign });
  const { user } = useAppContext();
  const { open } = useComposeEmail();
  const flags = useFlags();
  const canComposeEmail = !!flags?.composeEmail;

  const roleName = Object.values(RolesNames)?.[user.Role - 1];

  return (
    <Drawer
      classes={{
        paper: clsx(classes.drawer, {
          [classes.drawerCollapsed]: collapsedDrawer
        })
      }}
      variant="permanent">
      <SidebarContent>
        <Box>
          {canComposeEmail && (
            <Box className={classes.composeEmailWrapper}>
              <MenuButton
                onClick={() => open({})}
                icon={<EmailIconV2 fill={TextColor.Blue} />}
                label="Compose"
                customVariant={
                  collapsedDrawer
                    ? CustomMenuVariant.Collapsed
                    : CustomMenuVariant.Extended
                }
              />
            </Box>
          )}
          <MenuItems
            expandedSidebarMenuItem={expandedSidebarMenuItem}
            setExpandedSidebarMenuItem={setExpandedSidebarMenuItem}
            collapsedDrawer={collapsedDrawer}
          />
        </Box>
        {roleName && (
          <Typography
            style={{
              fontSize: 12,
              padding: '20px 0',
              color: TextColor.SlateGrey,
              textWrap: 'wrap',
              textAlign: 'center'
            }}>
            {roleName}
          </Typography>
        )}
      </SidebarContent>
    </Drawer>
  );
};
